import {
    getInterpolatedLineLayerWidth,
    getInterpolatedSelectableLineLayerWidth,
    getLineLayerConfig,
    INTERPOLATION_ZOOM_MAX,
    MAX_LINE_OFFSET,
    MIN_LINE_OFFSET,
    LINE_LAYER_SIZE,
    SELECTED_LINE_LAYER_SIZE,
    INTERPOLATION_ZOOM_MIN,
    LINE_LAYER_SIZE_OFFSET,
} from "@l2r-front/l2r-map"
import { selectLayerColorOpacity } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../common/constants/i18n"

const DEFAULT_LAYER_OPACITY = 0.5
const LINE_OFFSET_TRANSITION_ZOOM = 12

const getLineOffsetInterpolation = () => [
    "interpolate",
    ["linear"],
    ["zoom"],
    1,
    MIN_LINE_OFFSET,
    LINE_OFFSET_TRANSITION_ZOOM,
    MIN_LINE_OFFSET,
    INTERPOLATION_ZOOM_MAX,
    MAX_LINE_OFFSET,
]

const getConditionBasedLineSortKey = () => [
    "case",
    ["!=", ["typeof", ["get", "condition", ["properties"]]], "number"],
    0,
    1,
]

export const roadsideLegend = [
    {
        value: 1,
        color: "_components/legend/referential/trace",
        labelKey: "containers.roadside.legend.scale.presence",
    },
    {
        value: 10,
        color: "brand logiroad/primary/500",
        labelKey: "containers.roadside.legend.scale.good",
    },
    {
        value: 20,
        color: "_components/legend/DPE/orange",
        labelKey: "containers.roadside.legend.scale.poor",
    },
    {
        value: 30,
        color: "error",
        labelKey: "containers.roadside.legend.scale.serious",
    },
]

const buildRoadsideLegendStyles = (theme, selectedRoad = null) => {
    const legendStyles = roadsideLegend.flatMap((legendItem) => {
        const color = theme.palette[legendItem.color]?.main
        return [
            ["==", ["get", "condition"], legendItem.value],
            selectLayerColorOpacity(color, DEFAULT_LAYER_OPACITY, selectedRoad),
        ]
    })

    const defaultColor = theme.palette[roadsideLegend[0].color]?.main
    legendStyles.push(selectLayerColorOpacity(defaultColor, DEFAULT_LAYER_OPACITY, selectedRoad))

    return legendStyles
}

export const getRoadsideLegendConfig = (t, theme) => {
    return roadsideLegend.map(legendItem => {
        return {
            color: theme.palette[legendItem.color]?.main,
            label: t(I18N_NAMESPACE, legendItem.labelKey),
        }
    })
}

export const getRoadsideLayerStyle = (theme, selectedRoad = null) => {
    const legendStyles = buildRoadsideLegendStyles(theme, selectedRoad)

    return getLineLayerConfig({
        "paint": {
            "line-color": ["case", ...legendStyles],
            "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad),
        },
        "filter": ["all",
            ["all",
                ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], "string"],
                ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad]],
        ],
    })
}

export const getRoadRoadsideLayerStyle = (referentialColor, theme, selectedRoad = null) => {
    const legendStyles = buildRoadsideLegendStyles(theme, selectedRoad)

    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["!=", ["typeof", ["get", "condition", ["properties"]]], "number"],
                referentialColor,
            ].concat(legendStyles),
            "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
            "line-offset": getLineOffsetInterpolation(),
        },
        "layout": {
            "line-sort-key": getConditionBasedLineSortKey(),
        },
        "filter": ["all",
            ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad],
        ],
    })
}

const getRoadRoadsideEditLayerStyle = (referentialColor, disabledReferentialColor, legend, disabledLegend, selectedRoad = null) => {
    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["==", ["typeof", ["get", "locked", ["properties"]]], "boolean"],
                [
                    "case",
                    ["!=", ["typeof", ["get", "condition", ["properties"]]], "number"],
                    disabledReferentialColor,
                    ...disabledLegend,
                ],
                ["!=", ["typeof", ["get", "condition", ["properties"]]], "number"],
                referentialColor,
                ...legend,
            ],
            "line-width": ["interpolate",
                ["linear"],
                ["zoom"],
                1,
                1,
                INTERPOLATION_ZOOM_MIN,
                [
                    "case",
                    ["==", ["typeof", ["get", "locked", ["properties"]]], "boolean"],
                    LINE_LAYER_SIZE - LINE_LAYER_SIZE_OFFSET,
                    SELECTED_LINE_LAYER_SIZE - LINE_LAYER_SIZE_OFFSET,
                ],
                INTERPOLATION_ZOOM_MAX,
                [
                    "case",
                    ["==", ["typeof", ["get", "locked", ["properties"]]], "boolean"],
                    LINE_LAYER_SIZE,
                    SELECTED_LINE_LAYER_SIZE,
                ],
            ],
        },
        "layout": {
            "line-sort-key": getConditionBasedLineSortKey(),
        },
        "filter": ["all",
            ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad],
        ],
    })
}

export const getRoadRoadsidePresenceLayerStyle = (referentialColor, disabledReferentialColor, theme, type, selectedRoad = null) => {
    const legendStyles = [
        ["==", ["get", "roadsideInventoryType", ["properties"]], ["literal", type.code]],
        referentialColor,
        theme.palette["_components/legend/referential/other"]?.main,
    ]

    return getRoadRoadsideEditLayerStyle(referentialColor, disabledReferentialColor, legendStyles, legendStyles, selectedRoad)
}

export const getRoadRoadsideStateLayerStyle = (referentialColor, disabledReferentialColor, theme, selectedRoad = null) => {
    const legendStyles = buildRoadsideLegendStyles(theme, selectedRoad)
    const disabledLegendStyles = buildRoadsideLegendStyles(theme, "N/A")

    return getRoadRoadsideEditLayerStyle(referentialColor, disabledReferentialColor, legendStyles, disabledLegendStyles, selectedRoad)
}
